import React from "react";
import { useWidgetConfiguration } from "./configuration";
import { useWidgetBusData } from "./bus";
/**
 * get a the fetch function from the widget configuration.
 */
export function useFetch() {
    const widgetConfiguration = useWidgetConfiguration();
    return widgetConfiguration.fetch;
}
function computeMissingScopes(requiredScopes, grantedScopes) {
    if (requiredScopes.length === 0 || !grantedScopes) {
        return [];
    }
    const grantedScopesList = grantedScopes.split(/\s+/);
    return requiredScopes.filter((s) => grantedScopesList.indexOf(s) === -1);
}
/**
 * Get a fetch function that will automatically send the SG Connect token as
 * part of all calls.
 *
 * @param requiredScopes an optional list of scopes your call is going to
 *  require. If they are not granted, no fetch function will be returned.
 *  Can be a space separated string or a string array
 *
 * @returns An object with:
 * - a Fetch function or `null` if none could be constructed to satisfy your requirements
 * When fetch is `null` you get a status object with
 * - a reason indicating why no fetch is returned
 * - a list of scopes missing from your requiredScopes if the reason is "MISSING_SCOPES"
 */
export function useSgConnectFetch(requiredScopes) {
    const requiredScopesList = requiredScopes
        ? Array.isArray(requiredScopes)
            ? requiredScopes
            : requiredScopes.trim().split(/\s+/)
        : [];
    const fetch = useFetch();
    const sgConnectToken = useWidgetBusData("sg-connect.access-token");
    const grantedScopes = useWidgetBusData("sg-connect.granted-scope");
    const tokenRef = React.useRef(sgConnectToken);
    React.useEffect(() => {
        tokenRef.current = sgConnectToken;
    }, [sgConnectToken]);
    const authenticatedFetch = React.useCallback((input, init) => {
        const newInit = {
            ...init,
            headers: new Headers(init?.headers),
        };
        if (tokenRef.current)
            newInit.headers.append("Authorization", tokenRef.current);
        return fetch(input, newInit);
    }, [fetch]);
    const successResult = React.useMemo(() => ({
        fetch: authenticatedFetch,
        status: null,
    }), [authenticatedFetch]);
    const isTokenAvailable = !!sgConnectToken;
    const areScopesRequired = requiredScopesList.length > 0;
    const areScopesAvailable = !!grantedScopes;
    const missingScopes = computeMissingScopes(requiredScopesList, grantedScopes);
    const areScopesMissing = missingScopes.length > 0;
    /*
      Decision table to provide the correct result:
  
      isTokenAvailable | areScopesRequired | areScopesAvailable | areScopesMissing | OUT: fetch | OUT: status.reason | OUT: status.missingScopes
      FALSE            |   -               |  -                 |  -               | undefined  | NOT_CONNECTED      | []
      TRUE             | FALSE             |  -                 |  -               | fetch      | -                  | -
      TRUE             | TRUE              |  FALSE             |  -               | undefined  | NO_SCOPES_IN_BUS   | []
      TRUE             | TRUE              |  TRUE              |  TRUE            | undefined  | MISSING_SCOPES     | [api.missing-scope]
      TRUE             | TRUE              |  TRUE              |  FALSE           | fetch      | -                  | -
    */
    if (!isTokenAvailable) {
        return {
            fetch: null,
            status: { reason: "NOT_CONNECTED", missingScopes: [] },
        };
    }
    else {
        if (!areScopesRequired) {
            return successResult;
        }
        else {
            if (!areScopesAvailable) {
                return {
                    fetch: null,
                    status: {
                        reason: "NO_SCOPES_IN_BUS",
                        missingScopes: [],
                    },
                };
            }
            else if (areScopesMissing) {
                return {
                    fetch: null,
                    status: {
                        reason: "MISSING_SCOPES",
                        missingScopes,
                    },
                };
            }
            else {
                return successResult;
            }
        }
    }
}
